import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';

import palette from 'src/theme/palette';

import { formatCompactCurrency } from './formatters';
import type { RetentionCustomerEconomicsSegmentStat } from './types';

interface CustomerEconomicsTableProps {
  rows: RetentionCustomerEconomicsSegmentStat[];
}

const headCellSx = {
  color: palette.text.secondary,
  fontWeight: 700,
  fontSize: '0.8125rem',
  letterSpacing: '0.02em',
  borderBottom: `1px solid ${palette.grey[200]}`,
  py: 1.25,
  whiteSpace: 'nowrap',
} as const;

const bodyCellSx = {
  borderBottom: `1px solid ${palette.grey[200]}`,
  py: 1.35,
  verticalAlign: 'middle',
} as const;

export function CustomerEconomicsTable({ rows }: CustomerEconomicsTableProps) {
  return (
    <Box
      sx={{
        width: '100%',
        minWidth: 0,
        maxWidth: '100%',
        boxSizing: 'border-box',
      }}
    >
      <TableContainer
        sx={{
          width: '100%',
          maxWidth: '100%',
          overflowX: 'auto',
          WebkitOverflowScrolling: 'touch',
          borderRadius: 1,
        }}
      >
        <Table
          size="small"
          sx={{
            tableLayout: 'fixed',
            width: '100%',
            minWidth: 760,
          }}
        >
          <colgroup>
            <col style={{ width: '26%' }} />
            <col style={{ width: '12%' }} />
            <col style={{ width: '12%' }} />
            <col style={{ width: '16%' }} />
            <col style={{ width: '16%' }} />
            <col style={{ width: '18%' }} />
          </colgroup>
          <TableHead>
            <TableRow>
              <TableCell sx={{ ...headCellSx, pl: 0.5 }}>Segment</TableCell>
              <TableCell align="right" sx={headCellSx}>
                Customers
              </TableCell>
              <TableCell align="right" sx={headCellSx}>
                Avg orders
              </TableCell>
              <TableCell align="right" sx={headCellSx}>
                Avg revenue
              </TableCell>
              <TableCell align="right" sx={headCellSx}>
                Avg margin
              </TableCell>
              <TableCell align="right" sx={{ ...headCellSx, pr: 0.5 }}>
                LTV
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((row) => (
              <TableRow
                key={row.segment}
                hover
                sx={{
                  '&:last-of-type .MuiTableCell-root': {
                    borderBottom: 'none',
                  },
                }}
              >
                <TableCell sx={{ ...bodyCellSx, pl: 0.5 }}>
                  <Typography
                    sx={{ fontWeight: 700, color: palette.text.primary }}
                  >
                    {row.label}
                  </Typography>
                </TableCell>
                <TableCell align="right" sx={bodyCellSx}>
                  <Typography
                    sx={{ fontWeight: 700, color: palette.text.primary }}
                  >
                    {row.customers.toLocaleString()}
                  </Typography>
                </TableCell>
                <TableCell align="right" sx={bodyCellSx}>
                  <Typography
                    sx={{ fontWeight: 600, color: palette.text.secondary }}
                  >
                    {row.avgOrders.toFixed(1)}
                  </Typography>
                </TableCell>
                <TableCell align="right" sx={bodyCellSx}>
                  <Typography
                    sx={{ fontWeight: 600, color: palette.text.secondary }}
                  >
                    {formatCompactCurrency(row.avgRevenue)}
                  </Typography>
                </TableCell>
                <TableCell align="right" sx={bodyCellSx}>
                  <Typography
                    sx={{ fontWeight: 600, color: palette.text.secondary }}
                  >
                    {formatCompactCurrency(row.avgMargin)}
                  </Typography>
                </TableCell>
                <TableCell align="right" sx={{ ...bodyCellSx, pr: 0.5 }}>
                  <Typography
                    sx={{ fontWeight: 700, color: palette.text.primary }}
                  >
                    {formatCompactCurrency(row.ltv)}
                  </Typography>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Typography
        variant="caption"
        sx={{
          color: palette.text.secondary,
          display: 'block',
          mt: 1.25,
          lineHeight: 1.45,
          fontSize: '0.75rem',
        }}
      >
        This makes the retention story obvious at a glance: repeat buyers place
        more orders and compound more value, which is why payback can look much
        better beyond the first week.
      </Typography>
    </Box>
  );
}

export default CustomerEconomicsTable;
